/**
 * The bar across the top of every page: where you are, a place to search from,
 * and the three controls that belong to the person rather than to the data.
 *
 * ## The scheme control is a menu, not a segmented switch
 *
 * Three states need three labels, and "System / Light / Dark" spelt out costs
 * about eleven characters of a bar that also has to hold a breadcrumb and a
 * search field on a 1280 pixel laptop. A menu spends one control's worth of
 * width and still names every option in words.
 *
 * It also settles the naming rule cleanly. The trigger is called "Colour
 * scheme" in all three states; the state lives on `aria-checked` on the items.
 * A button that renames itself "Dark" cannot be found by somebody looking for
 * the colour scheme and cannot be asked what it is currently set to.
 *
 * The tri-state matches `namu-app`'s `Appearance` exactly, and `preferences.ts`
 * explains why that is not a detail.
 */

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  ChevronRightIcon,
  DensityIcon,
  MenuIcon,
  MoonIcon,
  SunIcon,
  SystemIcon,
  UserIcon,
} from '@/components/icons/Icons'
import { DropdownMenu } from '@/components/overlays/DropdownMenu/DropdownMenu'
import type { MenuItem } from '@/components/overlays/DropdownMenu/DropdownMenu'
import { Button } from '@/components/primitives'
import type { Density, Scheme } from '../preferences'
import { useShell } from '../shell'
import styles from './Topbar.module.css'

export interface Crumb {
  label: string
  /** Omit on the last crumb: the page you are on is not a link to itself. */
  to?: string
}

export interface TopbarUser {
  name: string
  email: string
  /** The role in the current organization: owner, manager, field agent. */
  role?: string
}

export interface TopbarProps {
  /** Coarse to fine. The last entry is the current page. */
  breadcrumb: readonly Crumb[]
  /** The global search control. A slot, because search is not layout's job. */
  search?: ReactNode
  user: TopbarUser
  /** Appended to the user menu above Sign out: profile, preferences, help. */
  userMenuItems?: readonly MenuItem[]
  onSignOut(): void
  /** Page-level controls that belong in the chrome rather than in a PageHeader. */
  actions?: ReactNode
  className?: string
}

const SCHEME_ICON: Record<Scheme, typeof SunIcon> = {
  system: SystemIcon,
  light: SunIcon,
  dark: MoonIcon,
}

const SCHEME_LABEL: Record<Scheme, string> = {
  system: 'Match system',
  light: 'Light',
  dark: 'Dark',
}

const DENSITY_LABEL: Record<Density, string> = {
  compact: 'Compact',
  comfortable: 'Comfortable',
  relaxed: 'Relaxed',
}

const DENSITY_NOTE: Record<Density, string> = {
  compact: 'Most rows',
  comfortable: 'Default',
  relaxed: 'Easiest to read',
}

export function Topbar({
  breadcrumb,
  search,
  user,
  userMenuItems,
  onSignOut,
  actions,
  className,
}: TopbarProps) {
  const { drawer, setDrawerOpen, scheme, setScheme, density, setDensity } = useShell()
  const SchemeIcon = SCHEME_ICON[scheme]

  const schemeItems: MenuItem[] = (['system', 'light', 'dark'] as const).map((option) => ({
    kind: 'radio',
    id: `scheme-${option}`,
    label: SCHEME_LABEL[option],
    checked: scheme === option,
    onSelect: () => setScheme(option),
  }))

  const densityItems: MenuItem[] = (['compact', 'comfortable', 'relaxed'] as const).map(
    (option) => ({
      kind: 'radio',
      id: `density-${option}`,
      label: DENSITY_LABEL[option],
      note: DENSITY_NOTE[option],
      checked: density === option,
      onSelect: () => setDensity(option),
    }),
  )

  const userItems: MenuItem[] = [
    { kind: 'label', id: 'user-heading', label: user.email },
    ...(userMenuItems ?? []),
    { kind: 'separator', id: 'user-separator' },
    { kind: 'action', id: 'sign-out', label: 'Sign out', onSelect: onSignOut },
  ]

  const topbarClassName = [styles.topbar, className].filter(Boolean).join(' ')
  const last = breadcrumb.length - 1

  return (
    <div className={topbarClassName}>
      {drawer ? (
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          className={styles.menuButton}
          aria-label="Navigation"
          onClick={() => setDrawerOpen(true)}
        >
          <MenuIcon size={20} />
        </Button>
      ) : null}

      {/*
        A real ordered list. The separators are decoration drawn by CSS content
        and are not in the markup, so a screen reader reads four place names
        rather than "Projects slash Kano slash Knowledge".
      */}
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <ol className={styles.crumbs} role="list">
          {breadcrumb.map((crumb, index) => (
            <li className={styles.crumb} key={`${crumb.label}-${index}`}>
              {index > 0 ? (
                <ChevronRightIcon size={14} className={styles.crumbSeparator} />
              ) : null}
              {crumb.to && index !== last ? (
                <Link className={styles.crumbLink} to={crumb.to}>
                  {crumb.label}
                </Link>
              ) : (
                <span className={styles.crumbCurrent} aria-current={index === last ? 'page' : undefined}>
                  {crumb.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>

      {search ? <div className={styles.search}>{search}</div> : <span className={styles.spacer} />}

      <div className={styles.tools}>
        {actions ? <div className={styles.actions}>{actions}</div> : null}

        <DropdownMenu
          label="Colour scheme"
          align="end"
          triggerClassName={styles.iconTrigger}
          items={schemeItems}
          trigger={<SchemeIcon size={18} />}
        />

        <DropdownMenu
          label="Row density"
          align="end"
          triggerClassName={styles.iconTrigger}
          items={densityItems}
          trigger={<DensityIcon size={18} />}
        />

        <DropdownMenu
          /* Not the person's name. A menu labelled "Amina Bello" is a menu
             nobody can find by asking for the account menu, and the name is
             already the first thing inside it. */
          label="Account"
          align="end"
          triggerClassName={styles.userTrigger}
          items={userItems}
          trigger={
            <>
              <UserIcon size={18} />
              <span className={styles.userName}>{user.name}</span>
              {user.role ? <span className={styles.userRole}>{user.role}</span> : null}
            </>
          }
        />
      </div>
    </div>
  )
}
